import { useEffect, useRef, useState } from "react";
import { Navigate, useNavigate, useSearchParams } from "react-router-dom";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  Link,
  Stack,
  Typography,
} from "@mui/material";
import AddLocationAltOutlined from "@mui/icons-material/AddLocationAltOutlined";
import MapRounded from "@mui/icons-material/MapRounded";
import ViewListRounded from "@mui/icons-material/ViewListRounded";
import {
  getGoogleMapsApiKey,
  loadGoogleMaps,
  type FacilityMapFacility,
} from "@bw/shared/components";
import { useAuth } from "@bw/shared/hooks";
import { parseDecimal } from "@bw/shared/utils";
import type { PublicFacilityResponse } from "@bw/shared/types/generated";
import { Page } from "../../../common/components/Page";
import { SectionHeader } from "../../../common/components/SectionHeader";
import { QuickSearchField } from "../../../common/components/QuickSearchField";
import { FacilityFilterBar } from "../../../common/components/FacilityFilterBar";
import { useDistanceUnits } from "../../../common/hooks/useDistanceUnits";
import {
  formatDistance,
  radiusLabel,
  readRadiusKm,
  type MeasurementSystem,
} from "../../../common/distanceUnits";
import { MaterialFacilityMap } from "../../../common/components/MaterialFacilityMap";
import { useNearbyCenter } from "../../../common/hooks/useNearbyCenter";
import { useMyProfile } from "../../../common/query/useMyProfile";
import { withFacilityDiscoveryReturn } from "../../../common/facilityNavigation";
import {
  clearDiscoveryCenter,
  readDiscoveryCenter,
  writeDiscoveryCenter,
} from "../../../common/discoveryLocation";
import { useFacilities } from "../hooks/useFacilities";
import { FacilityDiscoveryCard } from "../components/FacilityDiscoveryCard";
import { AddPlaceDialog } from "../components/AddPlaceDialog";
import { CommunityPlaceCard } from "../components/CommunityPlaceCard";
import { visuallyHidden } from "../../../common/visuallyHidden";

type DiscoveryView = "list" | "map";
const ACTIVITIES = [
  ["court_reservation", "Court Reservation"],
  ["open_play", "Drop-in"],
  ["classes", "Classes & clinics"],
  ["coaching", "Private lessons"],
  ["memberships", "Memberships"],
] as const;

/**
 * Last-resort viewport used only when there is neither an allowed discovery
 * center nor a geocoded facility result. It is deliberately not any device's
 * precise location, so the map still mounts without leaking user coordinates.
 */
const DEFAULT_MAP_CENTER = { latitude: 39.5, longitude: -98.35 }; // ~US centroid

/** Project a FacilityResponse → the map's minimal point type (address coords). */
function toMapFacility(
  f: PublicFacilityResponse,
  system: MeasurementSystem,
): FacilityMapFacility {
  // Popup subtitle: the distance when we have it (same format as the list card),
  // else the city/state so the pin still says where the facility is.
  const city = f.address?.city;
  const subtitle =
    formatDistance(f.distance_km, system, " away") ??
    (city ? [city, f.address?.state].filter(Boolean).join(", ") : undefined);
  // Pins read as short pills: the place's first words, flagged when it has a
  // live offer (the highlight colour).
  const shortName = f.name.split(/\s+/).slice(0, 3).join(" ");
  const hasOffer = (f.promotion_summaries?.length ?? 0) > 0;
  return {
    facility_id: f.facility_id,
    name: f.name,
    latitude: f.address?.latitude ?? undefined,
    longitude: f.address?.longitude ?? undefined,
    subtitle,
    pinLabel: hasOffer ? `Offer · ${shortName}` : shortName,
    highlight: hasOffer,
  };
}

/** Floating map buttons: a raised pill readable over the basemap. */
const mapControlSx = {
  borderRadius: 999,
  px: 1.5,
  bgcolor: "background.paper",
  border: 1,
  borderColor: "divider",
  boxShadow: 2,
  "&:hover": { bgcolor: "background.paper" },
} as const;

function hasMapCoordinates(
  facility: FacilityMapFacility,
): facility is FacilityMapFacility & { latitude: number; longitude: number } {
  return (
    typeof facility.latitude === "number" &&
    Number.isFinite(facility.latitude) &&
    typeof facility.longitude === "number" &&
    Number.isFinite(facility.longitude)
  );
}

/**
 * L4 facility-select container. Browse facilities a player can book at, then
 * navigate into one facility's HOMEPAGE (/facility/:facilityId) — the
 * hub that carries the "Book a court" CTA into the court sheet. Honest empty +
 * error states; zero fabricated results.
 */
export function PlayPage() {
  const [searchParams] = useSearchParams();
  const legacyQuery = searchParams.get("q");
  if (legacyQuery !== null) {
    const q = legacyQuery.trim();
    if (q) {
      const global = new URLSearchParams({ q });
      const city = searchParams.get("city")?.trim();
      if (city) global.set("city", city);
      return <Navigate to={`/search?${global}`} replace />;
    }
    const rest = new URLSearchParams(searchParams);
    rest.delete("q");
    return <Navigate to={rest.size ? `/play?${rest}` : "/play"} replace />;
  }
  return <PlayFacilityDirectory />;
}

function PlayFacilityDirectory() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [addPlaceOpen, setAddPlaceOpen] = useState(false);
  const city = searchParams.get("city") ?? "";
  const locationDisabled = searchParams.get("location") === "any";
  // Router search changes can commit after the local storage event. Suppress
  // reseeding synchronously when the user explicitly clears/chooses a city.
  const suppressHomeSeed = useRef(false);
  const { measurementSystem } = useDistanceUnits();
  const activity = ACTIVITIES.find(
    ([id]) => id === searchParams.get("activity"),
  )?.[0];
  const openNowParam = searchParams.get("open_now");
  const openNow =
    openNowParam === "true" ||
    (openNowParam === null && activity === "open_play");
  const specialOffers = searchParams.get("special_offers") === "true";
  const selectedRadius = readRadiusKm(
    searchParams.get("radius_km"),
    measurementSystem,
  );
  const requestedMinCourts = Number(searchParams.get("min_courts"));
  const minCourts =
    Number.isInteger(requestedMinCourts) &&
    requestedMinCourts >= 0 &&
    requestedMinCourts <= 100
      ? requestedMinCourts
      : 0;
  const page = Math.max(1, Math.floor(Number(searchParams.get("page")) || 1));
  const pageSize = 20;
  const updateSearch = (values: Record<string, string>) => {
    setSearchParams((previous) => {
      const next = new URLSearchParams(previous);
      next.delete("focus");
      for (const [key, value] of Object.entries(values)) {
        if (value) next.set(key, value);
        else next.delete(key);
      }
      return next;
    });
  };
  const returnToResult = (facilityId: string) => {
    const next = new URLSearchParams(searchParams);
    next.set("focus", facilityId);
    return `/play?${next}`;
  };
  // List ⇆ Map discovery view (B2). List is the default; the map mounts only
  // when the user switches to it (so the maps script isn't loaded eagerly).
  const view: DiscoveryView =
    searchParams.get("view") === "map" ? "map" : "list";
  const mapAvailable = Boolean(getGoogleMapsApiKey());
  const effectiveView: DiscoveryView =
    view === "map" && mapAvailable ? "map" : "list";
  // The discovery center is shared with /dropin (one client-side store). When
  // set, the search returns distance_km + nearest-first; FacilityPickerCard
  // renders the distance. No center → no coords → no distance (honesty guard).
  const { center, setCenter, clearCenter } = useNearbyCenter();
  const urlCenter = readDiscoveryCenter(searchParams);
  const selectedCenter = urlCenter ?? center;
  const { user, loading: authLoading } = useAuth();
  const { data: profile, isSuccess: profileLoaded } = useMyProfile(
    Boolean(user),
  );
  const openAddPlace = () => {
    if (!user) {
      const returnUrl = `/play?${searchParams.toString()}`;
      navigate(`/login?${new URLSearchParams({ returnUrl }).toString()}`);
      return;
    }
    setAddPlaceOpen(true);
  };

  // An eligible adult's saved home area is a default, never an override of an
  // explicit city, device center, or cleared-location choice. Minors use City:
  // the backend intentionally strips every coordinate for that cohort.
  const homeLat = parseDecimal(profile?.home_latitude);
  const homeLng = parseDecimal(profile?.home_longitude);
  useEffect(() => {
    if (
      !suppressHomeSeed.current &&
      !locationDisabled &&
      !city &&
      profileLoaded &&
      profile?.requires_parental_consent === false &&
      selectedCenter === null &&
      homeLat !== null &&
      homeLng !== null
    ) {
      setCenter({
        latitude: homeLat,
        longitude: homeLng,
        label: profile?.home_area_label ?? undefined,
        coarse: true,
      });
    }
  }, [
    selectedCenter,
    city,
    locationDisabled,
    profileLoaded,
    homeLat,
    homeLng,
    profile?.home_area_label,
    profile?.requires_parental_consent,
    setCenter,
  ]);

  // Fail closed for unresolved/errored authenticated profiles and minors,
  // including a persisted center from another identity on a shared device.
  const allowPreciseCenter =
    !authLoading &&
    (user === null ||
      (profileLoaded && profile?.requires_parental_consent === false));
  const centerAllowed =
    !locationDisabled && !city && selectedCenter !== null && allowPreciseCenter;
  const hasSuppressedPreciseCenter =
    selectedCenter !== null &&
    selectedCenter.coarse !== true &&
    !allowPreciseCenter;

  // The center the search actually uses (B1): the persisted center ONLY when
  // allowed (coarse always, precise only for adult/consented); otherwise none.
  // The map reuses this SAME effective center so a minor's suppressed precise
  // center never re-enters via the map (ADR-024). When there's no allowed center
  // the map must still never reuse the suppressed device point.
  const effectiveCenter =
    centerAllowed && selectedCenter
      ? {
          latitude: selectedCenter.latitude,
          longitude: selectedCenter.longitude,
        }
      : undefined;
  const {
    data: resultPage,
    isLoading,
    isError,
    refetch,
  } = useFacilities({
    city: city || undefined,
    service: activity,
    latitude: effectiveCenter?.latitude,
    longitude: effectiveCenter?.longitude,
    radius_km: effectiveCenter ? selectedRadius : undefined,
    open_now: openNow || undefined,
    special_offers: specialOffers || undefined,
    min_courts: minCourts || undefined,
    limit: pageSize,
    offset: (page - 1) * pageSize,
  });
  const showMapView = effectiveView === "map" && !isError && !isLoading;
  const data = resultPage?.items;
  const restoredResult = useRef({ key: "", restored: false });
  useEffect(() => {
    const facilityId = searchParams.get("focus");
    const contextKey = searchParams.toString();
    if (restoredResult.current.key !== contextKey) {
      restoredResult.current = { key: contextKey, restored: false };
    }
    if (
      effectiveView !== "list" ||
      !facilityId ||
      restoredResult.current.restored ||
      !data?.some((facility) => facility.facility_id === facilityId)
    )
      return;
    const element = document.getElementById(
      `facility-result-${encodeURIComponent(facilityId)}`,
    );
    if (!element) return;
    restoredResult.current.restored = true;
    element.focus({ preventScroll: true });
    element.scrollIntoView?.({ block: "center" });
  }, [data, searchParams, effectiveView]);

  // Project the SAME cached facility rows → map points (no refetch on toggle).
  const mapFacilities = (data ?? []).map((facility) =>
    toMapFacility(facility, measurementSystem),
  );
  const firstMappedFacility = mapFacilities.find(hasMapCoordinates);
  // With no user-selected area, open on a real returned marker so the map is
  // immediately useful. Facility coordinates are public venue data and do not
  // weaken the minor-location gate above. While a precise point is actively
  // suppressed, retain the neutral viewport so that point can never reach the
  // map even when a returned facility happens to share the same coordinates.
  // The US centroid is otherwise only the no-geocoded-results fallback.
  const mapCenter =
    effectiveCenter ??
    (firstMappedFacility && !hasSuppressedPreciseCenter
      ? {
          latitude: firstMappedFacility.latitude,
          longitude: firstMappedFacility.longitude,
        }
      : DEFAULT_MAP_CENTER);

  let results: React.ReactNode;
  if (isLoading) {
    results = (
      <Stack
        direction="row"
        spacing={1.5}
        role="status"
        aria-live="polite"
        sx={{ py: 4, alignItems: "center", justifyContent: "center" }}
      >
        <CircularProgress size={22} />
        <Typography color="text.secondary">Loading facilities…</Typography>
      </Stack>
    );
  } else if (isError) {
    results = (
      <Alert
        severity="error"
        role="alert"
        action={
          <Button color="inherit" onClick={() => void refetch()}>
            Retry
          </Button>
        }
      >
        Couldn&apos;t load facilities. Please try again.
      </Alert>
    );
  } else if (!data || data.length === 0) {
    results = (
      <Alert
        severity="info"
        action={
          <Button
            color="inherit"
            onClick={() => {
              suppressHomeSeed.current = true;
              clearCenter();
              updateSearch({
                activity: "",
                open_now: "",
                radius_km: "",
                min_courts: "",
                page: "",
                city: "",
                location: "any",
                focus: "",
              });
            }}
          >
            Clear filters
          </Button>
        }
      >
        {resultPage?.truncated
          ? "More places may match. Narrow the city, distance, or activity to see a complete result."
          : "No places match these filters."}
      </Alert>
    );
  } else {
    results = (
      <Stack divider={<Divider flexItem />}>
        {data.map((f) => (
          <FacilityDiscoveryCard
            key={f.facility_id}
            facility={f}
            activity={activity}
            measurementSystem={measurementSystem}
            returnTo={returnToResult(f.facility_id)}
          />
        ))}
      </Stack>
    );
  }

  return (
    <Page>
      <Stack spacing={1.5} sx={{ width: "100%", maxWidth: 720, minWidth: 0 }}>
        <Typography component="h1" sx={visuallyHidden}>
          Play
        </Typography>
          <FacilityFilterBar
            searchSlot={<QuickSearchField presentation="play" />}
            coordinateSearchAllowed={allowPreciseCenter}
            service={activity}
            city={city}
            center={centerAllowed ? selectedCenter : null}
            hasLocation={Boolean(selectedCenter || city)}
            radiusKm={selectedRadius}
            openNow={openNow}
            specialOffers={specialOffers}
            minCourts={minCourts}
            measurementSystem={measurementSystem}
            onServiceChange={(value) =>
              updateSearch({
                activity: value,
                page: "",
                ...(value === "open_play" && openNowParam === null
                  ? { open_now: "true" }
                  : {}),
              })
            }
            onCityChange={(value) => {
              if (value) {
                suppressHomeSeed.current = true;
                clearCenter();
              }
              setSearchParams((previous) => {
                const next = value
                  ? clearDiscoveryCenter(previous)
                  : new URLSearchParams(previous);
                if (value) next.set("city", value);
                else next.delete("city");
                next.delete("page");
                next.delete("focus");
                if (value) next.set("location", "any");
                return next;
              });
            }}
            onRadiusChange={(value) =>
              updateSearch({ radius_km: String(value), page: "", focus: "" })
            }
            onOpenNowChange={(value) =>
              updateSearch({
                open_now: value ? "true" : "false",
                page: "",
                focus: "",
              })
            }
            onSpecialOffersChange={(value) =>
              updateSearch({
                special_offers: value ? "true" : "",
                page: "",
                focus: "",
              })
            }
            onMinCourtsChange={(value) =>
              updateSearch({
                min_courts: value ? String(value) : "",
                page: "",
                focus: "",
              })
            }
            onCenterChange={(next) => {
              setCenter(next);
              setSearchParams((previous) => {
                const updated = writeDiscoveryCenter(
                  previous,
                  next,
                  selectedRadius,
                );
                updated.delete("city");
                updated.delete("location");
                updated.delete("focus");
                return updated;
              });
            }}
            onClearLocation={() => {
              suppressHomeSeed.current = true;
              clearCenter();
              setSearchParams((previous) => {
                const next = clearDiscoveryCenter(previous);
                next.delete("city");
                next.set("location", "any");
                next.delete("focus");
                return next;
              });
            }}
            onClearAll={() => {
              suppressHomeSeed.current = true;
              clearCenter();
              setSearchParams((previous) => {
                const next = clearDiscoveryCenter(previous);
                for (const key of [
                  "city",
                  "activity",
                  "open_now",
                  "special_offers",
                  "min_courts",
                  "focus",
                ])
                  next.delete(key);
                next.set("location", "any");
                return next;
              });
            }}
          />

          <Stack
            direction="row"
            spacing={1}
            sx={{
              alignItems: "center",
              flexWrap: "wrap",
              rowGap: 0.5,
              px: 0.5,
              // The map view floats these controls over the map instead.
              ...(showMapView ? visuallyHidden : {}),
            }}
          >
            <Typography
              role="status"
              aria-live="polite"
              variant="body2"
              color="text.secondary"
              sx={{ flex: 1, minWidth: 0 }}
            >
              {resultPage
                ? `${resultPage.total}${resultPage.total_relation === "lower_bound" ? "+" : ""} ${resultPage.total === 1 && resultPage.total_relation === "exact" ? "place" : "places"}${
                    effectiveCenter
                      ? ` within ${radiusLabel(selectedRadius, measurementSystem)} of ${selectedCenter?.label || "your location"}`
                      : city
                        ? ` in ${city}`
                        : ""
                  }`
                : ""}
            </Typography>
            {showMapView ? null : (
              <Button
                size="small"
                startIcon={<AddLocationAltOutlined />}
                onClick={openAddPlace}
                disabled={authLoading}
              >
                Add place
              </Button>
            )}
            {mapAvailable && !showMapView ? (
              <Button
                size="small"
                startIcon={effectiveView === "map" ? <ViewListRounded /> : <MapRounded />}
                onClick={() => updateSearch({ view: effectiveView === "map" ? "list" : "map" })}
              >
                {effectiveView === "map" ? "List" : "Map"}
              </Button>
            ) : null}
          </Stack>
          {resultPage?.truncated && data?.length ? (
            <Alert severity="info">
              More places may match. Narrow the city, activity, or area to
              see a complete result count.
            </Alert>
          ) : null}
          <Box sx={{ minWidth: 0 }}>
            {effectiveView === "list" || isError || isLoading || !data?.length
              ? results
              : null}
            {showMapView ? (
              <Stack spacing={1} sx={{ minWidth: 0 }}>
                <MaterialFacilityMap
                  fill
                  showUserLocation={allowPreciseCenter && !locationDisabled}
                  overlay={
                    <Stack direction="row" spacing={1} sx={{ justifyContent: "space-between", alignItems: "flex-start" }}>
                      <Chip
                        aria-hidden
                        label={`${mapFacilities.filter(hasMapCoordinates).length} ${mapFacilities.filter(hasMapCoordinates).length === 1 ? "place" : "places"} on the map`}
                        sx={{ bgcolor: "background.paper", border: 1, borderColor: "divider", fontWeight: 600 }}
                      />
                      <Stack spacing={1} sx={{ alignItems: "flex-end" }}>
                        <Button
                          size="small"
                          startIcon={<ViewListRounded />}
                          onClick={() => updateSearch({ view: "list" })}
                          sx={mapControlSx}
                        >
                          List
                        </Button>
                        <Button
                          size="small"
                          startIcon={<AddLocationAltOutlined />}
                          onClick={openAddPlace}
                          disabled={authLoading}
                          sx={mapControlSx}
                        >
                          Add place
                        </Button>
                      </Stack>
                    </Stack>
                  }
                  facilities={mapFacilities}
                  center={mapCenter}
                  initialSelectedFacilityId={
                    searchParams.get("focus") ?? undefined
                  }
                  allowAreaSearch={allowPreciseCenter}
                  onSelectFacility={(f) =>
                    navigate(
                      withFacilityDiscoveryReturn(
                        `/facility/${f.facility_id}`,
                        returnToResult(f.facility_id),
                      ),
                    )
                  }
                  onSearchArea={(c) => {
                    if (!allowPreciseCenter) return;
                    const nextCenter = {
                      latitude: c.latitude,
                      longitude: c.longitude,
                    };
                    setCenter(nextCenter);
                    setSearchParams((previous) => {
                      const next = writeDiscoveryCenter(
                        previous,
                        nextCenter,
                        selectedRadius,
                      );
                      for (const key of ["focus", "city", "location"])
                        next.delete(key);
                      return next;
                    });
                  }}
                  // Pass the maps seam resolved HERE (this module's barrel import is
                  // mock-interceptable, unlike FacilityMap's own captured import).
                  getGoogleMapsApiKey={getGoogleMapsApiKey}
                  loadGoogleMaps={loadGoogleMaps}
                />
                {mapFacilities.some(
                  (facility) => !hasMapCoordinates(facility),
                ) ? (
                  <Alert severity="info">
                    Some facilities have no map location. Switch to List to see
                    all results.
                  </Alert>
                ) : null}
                <Typography variant="caption" color="text.secondary">
                  {!allowPreciseCenter
                    ? "Markers show this results page. Use the City filter to change your search area."
                    : effectiveCenter
                      ? `Searches within ${radiusLabel(selectedRadius, measurementSystem)} of your selected area. Markers show this results page.`
                      : `Markers show this results page. Move the map and choose Search this area to apply a ${radiusLabel(selectedRadius, measurementSystem)} radius.`}
                </Typography>
              </Stack>
            ) : null}
          </Box>
          {effectiveView === "list" && (resultPage?.places?.length ?? 0) > 0 ? (
            <Stack spacing={1.25}>
              <SectionHeader title="Community-added places" />
              <Stack divider={<Divider flexItem />}>
                {resultPage?.places?.map((place) => (
                  <CommunityPlaceCard key={place.place_id} place={place} />
                ))}
              </Stack>
            </Stack>
          ) : null}
          {resultPage && (page > 1 || resultPage.has_more) ? (
            <Stack direction="row" spacing={2} sx={{ alignItems: "center" }}>
              <Button
                disabled={page === 1}
                onClick={() => updateSearch({ page: String(page - 1) })}
              >
                Previous
              </Button>
              <Typography variant="body2">
                {resultPage.total_relation === "exact"
                  ? `Page ${page} of ${Math.max(1, Math.ceil(resultPage.total / pageSize))}`
                  : `Page ${page}`}
              </Typography>
              <Button
                disabled={!resultPage.has_more}
                onClick={() => updateSearch({ page: String(page + 1) })}
              >
                Next
              </Button>
            </Stack>
          ) : null}
          {effectiveView === "list" && !isLoading && !isError && data?.length ? (
            <Typography variant="body2" color="text.secondary" sx={{ px: 0.5 }}>
              Can&apos;t find a place?{" "}
              <Link component="button" type="button" onClick={openAddPlace} disabled={authLoading} sx={{ verticalAlign: "baseline", fontWeight: 600 }}>
                Add it
              </Link>
            </Typography>
          ) : null}
          {/* Mounted only while open: its Google Maps search loads nothing for
              visitors who never add a place (and none for minors browsing). */}
          {addPlaceOpen ? (
            <AddPlaceDialog
              open
              onClose={() => setAddPlaceOpen(false)}
              initialName={searchParams.get("q") ?? ""}
            />
          ) : null}
      </Stack>
    </Page>
  );
}
